import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import styles from './Pinput.module.css'

type PinputProps = {
  length?: number
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  label: string
  autoFocus?: boolean
  mask?: boolean
}

/**
 * Simplified port of slim's `Pinput` component.
 *
 * Renders `length` single-character inputs. Typing advances focus; Backspace
 * retreats. Pasting a `length`-character code distributes across all inputs.
 * Restricted to digits.
 *
 * `mask` renders the cells as password fields (for confirmation PINs) and opts
 * out of one-time-code autofill, which only makes sense for SMS codes.
 */
export const Pinput = ({
  length = 6,
  value,
  onChange,
  disabled = false,
  label,
  autoFocus = false,
  mask = false,
}: PinputProps) => {
  const { t } = useTranslation()
  const baseId = useId()
  const refs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus()
  }, [autoFocus])

  const setCharAt = useCallback(
    (index: number, char: string) => {
      const chars = value.padEnd(length, ' ').split('')
      chars[index] = char
      const next = chars.join('').replace(/\s+$/, '')
      onChange(next.slice(0, length))
    },
    [length, onChange, value]
  )

  const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    const digit = raw.replace(/\D/g, '').slice(-1)
    setCharAt(index, digit)
    if (digit) refs.current[index + 1]?.focus()
  }

  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index]) {
      refs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (!pasted) return
    e.preventDefault()
    onChange(pasted)
    refs.current[Math.min(pasted.length, length - 1)]?.focus()
  }

  return (
    <fieldset className={styles.root} aria-label={label}>
      <legend className={styles.srOnly}>{label}</legend>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={el => {
            refs.current[i] = el
          }}
          id={`${baseId}-${i}`}
          className={styles.cell}
          type={mask ? 'password' : 'text'}
          inputMode='numeric'
          autoComplete={!mask && i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          size={1}
          value={value[i] ?? ''}
          onChange={handleChange(i)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          disabled={disabled}
          aria-label={t('pinput.digitLabel', { label, index: i + 1 })}
        />
      ))}
    </fieldset>
  )
}
